'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { formatFileSize } from '@/lib/formatters/file.formatter'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { Button } from '@/ui/buttons/button/Button.client'
import { Icon } from '@/ui/icons/icon/Icon'
import type { ProductAttachment } from '@gql-generated/graphql'

type UploadedFileItemProps = UiComponentProps & {
  fileName: string
  fileSizeLabel: string
  onDelete?: () => void
  isDisabled?: boolean
}

function FileIcon({ fileName }: { fileName: string }) {
  const extension = fileName.split('.').pop()?.toLowerCase()
  const className = 'm-1 shrink-0 text-brand-2-900'

  switch (extension) {
    case 'pdf':
    case 'txt':
      return <Icon.FileText className={className} size="md" aria-hidden />
    case 'csv':
    case 'xlsx':
    case 'xls':
      return <Icon.FileSpreadsheet className={className} size="md" aria-hidden />
    case 'jpg':
    case 'jpeg':
    case 'png':
      return <Icon.Image className={className} size="md" aria-hidden />
    default:
      return <Icon.FileText className={className} size="md" aria-hidden />
  }
}

export function UploadedFileItem({
  id,
  'data-testid': dataTestId = 'uploaded-file-item',
  className,
  fileName,
  fileSizeLabel,
  onDelete,
  isDisabled,
}: UploadedFileItemProps) {
  const { t } = useTranslation(['uploadedFileItem'])

  return (
    <div
      id={id}
      data-testid={dataTestId}
      className={twMerge(className, 'flex w-full items-center gap-2 rounded-sm bg-brand-2-50 p-1')}
    >
      <div className="flex min-w-0 flex-1 items-center gap-1">
        <FileIcon fileName={fileName} />
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="w-full truncate font-bold text-brand-2-900" title={fileName}>
            {fileName}
          </span>
          {fileSizeLabel && (
            <span className="w-full text-sm text-brand-2-900">{fileSizeLabel}</span>
          )}
        </div>
      </div>
      {onDelete && (
        <Button
          size="sm"
          isRound
          variant="primary"
          iconLeft={<Icon.Trash2 className="!size-[1.125rem]" />}
          className="shrink-0"
          onPress={onDelete}
          isDisabled={isDisabled}
          aria-label={t('uploadedFileItem:delete', { fileName })}
        />
      )}
    </div>
  )
}

type UploadedFileItemFromAttachmentProps = {
  item: File | ProductAttachment
  onDelete?: (file: File) => void
  isDisabled?: boolean
} & StrictPick<UploadedFileItemProps, 'id' | 'data-testid' | 'className'>

export function UploadedFileItemFromAttachment({
  item,
  onDelete,
  isDisabled,
  ...props
}: UploadedFileItemFromAttachmentProps) {
  const isFile = item instanceof File
  const fileSizeLabel = formatFileSize(isFile ? item.size : item.length)

  return (
    <UploadedFileItem
      {...props}
      fileName={item.name}
      fileSizeLabel={fileSizeLabel}
      onDelete={
        onDelete && isFile
          ? () => {
              onDelete(item)
            }
          : undefined
      }
      isDisabled={isDisabled}
    />
  )
}
